import React from 'react';
import MenuButton from '../components/MenuButton';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { logout } from '../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';

const Dashboard: React.FC = () => {
    const user = useAppSelector((state) => state.auth.user);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const handleLogout = () => {
        dispatch(logout());
        navigate('/login');
    };

    return (
        <div className="flex-1 flex flex-col min-w-0 bg-background overflow-hidden">
            {/* Header */}
            <header className="h-24 bg-primary border-b border-cyan-600 flex items-center justify-between px-4 md:px-8 shadow-md z-20 sticky top-0 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 opacity-90"></div>
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>

                <div className="relative z-10 flex items-center gap-3 md:gap-6 w-full justify-between">
                    <div className="flex items-center gap-3 md:gap-6 min-w-0">
                        <MenuButton />
                        <div className="bg-white/10 backdrop-blur-md px-6 py-2 rounded-lg border border-white/20">
                            <h1 className="text-xl font-bold text-white flex items-center gap-3">
                                <i className="fa-solid fa-gauge"></i>
                                Dashboard
                            </h1>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="text-right">
                            <p className="text-sm font-bold text-white">{user?.name}</p>
                            <p className="text-xs text-white/80">{user?.email}</p>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="px-4 py-2 bg-white text-primary rounded-lg font-medium text-sm hover:bg-slate-50 transition-colors flex items-center gap-2 shadow-md"
                        >
                            <i className="fa-solid fa-right-from-bracket"></i>
                            <span>Logout</span>
                        </button>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 overflow-y-auto p-4 lg:p-6 bg-slate-50">
                <div className="max-w-7xl mx-auto">
                    {/* Welcome Card */}
                    <div className="bg-gradient-to-br from-blue-700 to-blue-800 rounded-2xl p-8 text-white mb-8 shadow-xl">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                                <i className="fa-solid fa-hand-wave text-3xl"></i>
                            </div>
                            <div>
                                <h2 className="text-3xl font-bold">Welcome back, {user?.name?.split(' ')[0]}!</h2>
                                <p className="text-cyan-100 text-sm md:text-lg">You're logged in as an Administrator</p>
                            </div>
                        </div>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <i className="fa-solid fa-users text-blue-600 text-xl"></i>
                                </div>
                                <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded">+12%</span>
                            </div>
                            <h3 className="text-2xl font-bold text-slate-800 mb-1">1,234</h3>
                            <p className="text-sm text-slate-500 font-medium">Total Users</p>
                        </div>

                        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <i className="fa-solid fa-dumbbell text-blue-600 text-xl"></i>
                                </div>
                                <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded">+8%</span>
                            </div>
                            <h3 className="text-2xl font-bold text-slate-800 mb-1">56</h3>
                            <p className="text-sm text-slate-500 font-medium">Active Trainers</p>
                        </div>

                        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center">
                                    <i className="fa-solid fa-building text-blue-500 text-xl"></i>
                                </div>
                                <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded">+3%</span>
                            </div>
                            <h3 className="text-2xl font-bold text-slate-800 mb-1">23</h3>
                            <p className="text-sm text-slate-500 font-medium">Members</p>
                        </div>

                        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
                                    <i className="fa-solid fa-chart-line text-blue-700 text-xl"></i>
                                </div>
                                <span className="text-xs font-bold text-blue-700 bg-blue-100 px-2 py-1 rounded">+24%</span>
                            </div>
                            <h3 className="text-2xl font-bold text-slate-800 mb-1">89%</h3>
                            <p className="text-sm text-slate-500 font-medium">Platform Health</p>
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                        <h3 className="text-xl font-bold text-slate-800 mb-6">Quick Actions</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <button
                                onClick={() => navigate('/')}
                                className="p-4 border-2 border-slate-200 rounded-lg hover:border-blue-600 hover:bg-blue-50 transition-all text-left group"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-slate-100 group-hover:bg-blue-100 rounded-lg flex items-center justify-center transition-colors">
                                        <i className="fa-solid fa-users-gear text-slate-600 group-hover:text-blue-700"></i>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-slate-800">Manage Users</h4>
                                        <p className="text-xs text-slate-500">View and manage all users</p>
                                    </div>
                                </div>
                            </button>

                            <button className="p-4 border-2 border-slate-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all text-left group">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-slate-100 group-hover:bg-blue-100 rounded-lg flex items-center justify-center transition-colors">
                                        <i className="fa-solid fa-building text-slate-600 group-hover:text-blue-600"></i>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-slate-800">Manage Members</h4>
                                        <p className="text-xs text-slate-500">Review and manage member accounts</p>
                                    </div>
                                </div>
                            </button>

                            <button className="p-4 border-2 border-slate-200 rounded-lg hover:border-blue-600 hover:bg-blue-50 transition-all text-left group">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-slate-100 group-hover:bg-blue-100 rounded-lg flex items-center justify-center transition-colors">
                                        <i className="fa-solid fa-chart-bar text-slate-600 group-hover:text-blue-700"></i>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-slate-800">View Reports</h4>
                                        <p className="text-xs text-slate-500">Analytics and insights</p>
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Dashboard;
